import type { ContextSection } from './enums';

/** PRJ-06 editable templates. Headings are the completeness checklist for each section. */
export const CONTEXT_TEMPLATES: Record<ContextSection, string> = {
  overview: `## Purpose\n\n## Users\n\n## Domain terminology\n\n## Scope\n\n## Acceptance conventions\n`,
  structure: `## Architecture\n\n## Modules\n\n## Commands\n\n## Tests\n\n## Coding conventions\n`,
  deployment: `## Target environments\n\n## Prerequisites\n\n## Delivery steps\n\n## Verification\n\n## Rollback\n`,
};

export const CONTEXT_REQUIRED_HEADINGS: Record<ContextSection, readonly string[]> = {
  overview: ['Purpose', 'Users', 'Domain terminology', 'Scope', 'Acceptance conventions'],
  structure: ['Architecture', 'Modules', 'Commands', 'Tests', 'Coding conventions'],
  deployment: ['Target environments', 'Prerequisites', 'Delivery steps', 'Verification', 'Rollback'],
};

/** Returns required headings that are absent or have no body text beneath them. */
export function missingContextHeadings(section: ContextSection, body: string | null | undefined): string[] {
  const required = CONTEXT_REQUIRED_HEADINGS[section];
  if (!body) return [...required];
  const lines = body.split(/\r?\n/);
  const missing: string[] = [];
  for (const heading of required) {
    const idx = lines.findIndex(
      (l) =>
        /^#{1,6}\s+/.test(l) &&
        l
          .replace(/^#{1,6}\s+/, '')
          .trim()
          .toLowerCase() === heading.toLowerCase(),
    );
    if (idx < 0) {
      missing.push(heading);
      continue;
    }
    let hasContent = false;
    for (let i = idx + 1; i < lines.length; i++) {
      const line = lines[i]!;
      if (/^#{1,6}\s+/.test(line)) break;
      if (line.trim()) {
        hasContent = true;
        break;
      }
    }
    if (!hasContent) missing.push(heading);
  }
  return missing;
}

/** Deployment context must not contain secret values (PRJ-06). Heuristic guard for obvious credentials. */
const SECRET_PATTERNS = [
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/,
  /\bgh[pousr]_[A-Za-z0-9]{30,}\b/,
  /\bxox[abpr]-[A-Za-z0-9-]{10,}\b/,
  /\b(?:password|passwd|secret|api[_-]?key|token)\s*[:=]\s*\S{8,}/i,
];
export const containsLikelySecret = (text: string) => SECRET_PATTERNS.some((p) => p.test(text));
